/**
 * TerraformSkillEffect — Earth tree stone_fist path apotheosis.
 *
 * Channel for 2 seconds. On completion, permanently reshapes the current
 * room. This implementation is a placeholder pending a full mini-spec:
 * it creates a cross pattern of indestructible wall tiles around the
 * player's position (durationMs: 0, hp: Infinity).
 *
 * Interruption (deactivate before channel completes) cancels with no effect.
 *
 * Cooldown tier: "very_long" (4000ms — no canonical ms yet, doubled from long).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Terraform — Apotheosis
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TerraformDeps {
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly terrain: {
    place(config: {
      type: string;
      position: { x: number; y: number };
      widthTiles: number;
      heightTiles: number;
      hp: number;
      durationMs: number;
      sourceSkillId: string;
      blocksMovement: boolean;
      blocksProjectiles: boolean;
    }): string;
  };
  readonly pathfinding: {
    markImpassable(tiles: { x: number; y: number }[]): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TerraformConfig {
  /** Channel hold duration in ms before effect fires. */
  readonly channelMs: number;
  /** Very_long cooldown tier — 4000ms. */
  readonly cooldownMs: number;
  /** World units per tile — used to compute cross-pattern offsets. */
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: TerraformConfig = {
  channelMs: 2000,
  cooldownMs: 4000,  // very_long tier
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type TerraformPhase = 'idle' | 'channeling' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TerraformSkillEffect implements SkillEffect {
  readonly skillId = 'terraform';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: TerraformDeps;
  private readonly config: TerraformConfig;

  private _phase: TerraformPhase = 'idle';
  private _phaseTimer = 0;
  private _channelComplete = false;
  private _terrainIds: string[] = [];
  private _pathfindingCallCount = 0;
  private _placedTiles: { x: number; y: number }[] = [];

  constructor(deps: TerraformDeps, config: Partial<TerraformConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): TerraformPhase { return this._phase; }
  get channelComplete(): boolean { return this._channelComplete; }
  get terrainIds(): readonly string[] { return this._terrainIds; }
  get pathfindingCallCount(): number { return this._pathfindingCallCount; }
  get placedTiles(): readonly { x: number; y: number }[] { return this._placedTiles; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle';
  }

  activate(): boolean {
    if (this._phase !== 'idle') return false;

    this._phase = 'channeling';
    this._phaseTimer = this.config.channelMs;
    this._channelComplete = false;
    this._terrainIds = [];
    this._placedTiles = [];

    return true;
  }

  /** Deactivate: if channeling, cancel the channel with no effect. */
  deactivate(): void {
    if (this._phase === 'channeling') {
      this._phase = 'idle';
      this._phaseTimer = 0;
    }
  }

  isActive(): boolean {
    return this._phase === 'channeling';
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    this._phaseTimer -= delta;

    if (this._phase === 'channeling' && this._phaseTimer <= 0) {
      this.onChannelComplete();
      this._phase = 'cooldown';
      this._phaseTimer = this.config.cooldownMs;
      return;
    }

    if (this._phase === 'cooldown' && this._phaseTimer <= 0) {
      this._phase = 'idle';
      this._phaseTimer = 0;
    }
  }

  destroy(): void {
    this._phase = 'idle';
    this._phaseTimer = 0;
  }

  // --- Internal ---

  private onChannelComplete(): void {
    this._channelComplete = true;

    const { x, y } = this.deps.player.getPosition();
    const t = this.config.tileSizePx;

    // Placeholder: cross pattern — center tile plus the four cardinal neighbours
    const offsets = [
      { dx: 0, dy: 0 },
      { dx: -t, dy: 0 },
      { dx: t, dy: 0 },
      { dx: 0, dy: -t },
      { dx: 0, dy: t },
    ];

    const tiles: { x: number; y: number }[] = [];
    for (const { dx, dy } of offsets) {
      const pos = { x: x + dx, y: y + dy };
      const id = this.deps.terrain.place({
        type: 'wall',
        position: pos,
        widthTiles: 1,
        heightTiles: 1,
        hp: Infinity,
        durationMs: 0,
        sourceSkillId: 'terraform',
        blocksMovement: true,
        blocksProjectiles: true,
      });
      this._terrainIds.push(id);
      tiles.push(pos);
    }

    this._placedTiles = tiles;
    this.deps.pathfinding.markImpassable(tiles);
    this._pathfindingCallCount++;
  }
}
